/**
 * The panel's orchestration: session, transcript, sending, unread.
 *
 * One composable rather than a state library, and the reason is size rather
 * than taste. This app has one screen with one conversation on it; a query cache and a
 * router would be more machinery than the thing they manage, in a bundle a
 * visitor downloads before they have decided whether they want to talk to anyone.
 *
 * NO SSE YET. Replies arrive by polling. P2-06 defines the streaming path, and
 * defines it carefully, because the widget scope is the ONE place a frame may
 * carry a message BODY — under its own per-scope payload schema rather than a
 * global loosening. Until that lands, this polls on an interval that slows down
 * when the panel is closed, so a visitor who never opens the widget pays almost
 * nothing for it. A stated gap, not a design.
 */
import {
  computed,
  onScopeDispose,
  reactive,
  ref,
  shallowRef,
  watch,
  watchEffect,
  type Ref,
} from 'vue';

import type { WidgetLocale } from '../i18n/locales';

import { ApiProblemError, hasVisitorToken, setVisitorToken } from '../api/client';
import type {
  SeqString,
  WidgetAppearance,
  WidgetConversation,
  WidgetConversationSummary,
  WidgetMessage,
  WidgetPrechatField,
} from '../api/contract';
import { clearCachedSession, readCachedSession, writeCachedSession } from '../api/session-store';
import {
  closeConversation,
  endSession,
  fetchHistory,
  fetchPastThread,
  fetchThread,
  openSession,
  putVisitorContext,
  sendMessage,
} from '../api/widget';
import { resolveWidgetLocale } from '../i18n/locale';
import { mergeHistory } from '../model/history';
import { newClientMessageId } from '../model/ids';
import { grant, revoke, type ConsentPurpose, type ConsentState } from '../privacy/consent';
import { readConsent, writeConsent } from '../privacy/consent-store';
import { availablePurposes, visitorContextBody } from '../privacy/visitor-report';
import { readVisitorSignals } from '../privacy/visitor-context';
import {
  clearByClientMessageId,
  clearConfirmed,
  compareSeq,
  latestOutboundSeq,
  latestSeq,
  reconcileThread,
  unreadCount,
  ZERO_SEQ,
  type PendingMessage,
} from '../model/transcript';
import { orderedFields, prechatPayload, type PrechatValues } from '../prechat/fields';
import { createChime } from '../sound/chime';

import {
  IDENTITY_OK,
  identityFailure,
  isStaleBearer,
  jwtSubject,
  type IdentityOutcome,
} from './identity';
import { readSeenSeq, writeSeenSeq } from './seen';

/** Open: the visitor is watching, so latency is visible. Closed: only the badge is. */
const POLL_INTERVAL_OPEN_MS = 8_000;
const POLL_INTERVAL_CLOSED_MS = 45_000;

/** A screenful. The whole transcript is a support export, not a widget concern. */
const THREAD_PAGE_SIZE = 50;

/**
 * A page of history — the route's own default, and about two screenfuls on a
 * phone. The rest arrives when the visitor scrolls to the bottom of the list,
 * which most of them never will.
 */
const HISTORY_PAGE_SIZE = 20;

/**
 * The transcript a `fresh` visitor sees: shared, so that the view does not hand
 * a NEW empty array to every reader each time `fresh` is re-read, and re-run
 * every watcher that depends on `messages`.
 */
const NO_MESSAGES: readonly WidgetMessage[] = [];

/**
 * One failed poll is a tunnel, a lift or a laptop lid. Three in a row is a
 * problem worth telling the visitor about, because by then they may have typed
 * something and be waiting for a reply that is not coming.
 */
const POLL_FAILURES_BEFORE_OFFLINE = 3;

export type WidgetPhase =
  | { readonly kind: 'booting' }
  /** The widget cannot run here at all: unknown or revoked key, origin refused. */
  | { readonly kind: 'unavailable' }
  /** We reached the API once and cannot now. Recoverable, and the visitor may retry. */
  | { readonly kind: 'offline' }
  | { readonly kind: 'prechat' }
  | { readonly kind: 'ready' };

/**
 * Which of the three screens the panel is showing — P2-52.
 *
 * A UNION AND NOT A ROUTER. The panel has one dialog, no URL of its own (it is
 * an iframe whose address the host page owns) and three states, so a router
 * would be a dependency, a history stack and a bundle cost in service of a back
 * button that already exists in the header. `past` carries its id in the state
 * rather than beside it, which is what makes "which conversation is open?"
 * unanswerable while the screen is `chat`.
 */
export type WidgetScreen =
  | { readonly kind: 'chat' }
  | { readonly kind: 'history' }
  | { readonly kind: 'past'; readonly conversationId: string };

/** The visitor's past conversations, paged. Loaded when the screen is opened. */
export interface WidgetHistoryView {
  readonly items: readonly WidgetConversationSummary[];
  readonly loading: boolean;
  readonly failed: boolean;
  /** A `nextBefore` is held, so there is another page to ask for. */
  readonly hasMore: boolean;
  /** The next page — and, after a failure, the retry for the page that failed. */
  readonly loadMore: () => void;
}

/** One past conversation, read-only. `conversation` is null until it arrives. */
export interface WidgetPastView {
  readonly conversation: WidgetConversation | null;
  readonly messages: readonly WidgetMessage[];
  readonly loading: boolean;
  readonly failed: boolean;
}

export interface WidgetView {
  readonly phase: WidgetPhase;
  readonly appearance: WidgetAppearance | null;
  readonly prechatFields: readonly WidgetPrechatField[];
  readonly conversation: WidgetConversation | null;
  readonly locale: WidgetLocale;
  readonly messages: readonly WidgetMessage[];
  readonly pending: readonly PendingMessage[];
  readonly unread: number;
  readonly submitting: boolean;
  readonly screen: WidgetScreen;
  readonly history: WidgetHistoryView;
  readonly past: WidgetPastView;
  /**
   * The visitor asked to start again after a close, and the server does not
   * know yet.
   *
   * There is nothing to tell it: `widget.messages.send` creates the new
   * conversation itself, on the first message, because `closed` is terminal for
   * the visitor (ADR-0142) and the server appends only to a conversation that is
   * not. So this is a purely local "show me a blank page": `conversation` is
   * STILL the closed one underneath, and what changes is that `messages` reads
   * empty and the composer comes back. It ends when a conversation with a
   * different id arrives — which is to say when the send that was the whole
   * point of it succeeds.
   */
  readonly fresh: boolean;
  /** A close is in flight; the confirm button waits rather than firing twice. */
  readonly closing: boolean;
  /**
   * What this visitor has decided about the two optional purposes, in this tab.
   *
   * On the view rather than hidden inside the composable because the notice needs all
   * THREE states `wasAsked` distinguishes — ask, granted, refused — and a
   * boolean per purpose could not carry the third. The panel does not interpret
   * it: `isGranted` and `wasAsked` are the only readers, and they live in
   * `privacy/consent.ts` so that "does this still count under the current
   * notice?" has exactly one answer.
   */
  readonly consent: ConsentState;
  /**
   * The purposes there is any point asking about — the ones whose data the host
   * page actually supplied. Empty today, and `availablePurposes` says why.
   */
  readonly consentPurposes: readonly ConsentPurpose[];
  readonly grantConsent: (purpose: ConsentPurpose) => void;
  readonly refuseConsent: (purpose: ConsentPurpose) => void;
  readonly setLocale: (locale: WidgetLocale) => void;
  readonly submitPrechat: (values: PrechatValues) => void;
  readonly send: (body: string) => void;
  readonly retry: (clientMessageId: string) => void;
  readonly reconnect: () => void;
  readonly openHistory: () => void;
  readonly openPast: (conversationId: string) => void;
  readonly backToChat: () => void;
  readonly endChat: () => void;
  readonly startNewConversation: () => void;
  /**
   * The site's player signed in (ADR-0249 §4): the next session is minted from
   * this JWT. Resolves with the outcome the loader's promise reports; never
   * rejects.
   */
  readonly identify: (jwt: string) => Promise<IdentityOutcome>;
  /** The site's player signed out: end the session, forget the device, start anonymous. */
  readonly logout: () => Promise<IdentityOutcome>;
}

export interface WidgetInput {
  readonly publicKey: string;
  /** From `data-omni-locale`, via the frame URL. */
  readonly requestedLocale: string | null;
  /**
   * The embedding page's origin, from `event.origin` of the handshake. null while
   * the handshake is outstanding, and nothing is requested before it arrives —
   * see `src/app/host.ts` for why that value is only trustworthy from there.
   * A ref, because it arrives after setup and everything below waits on it.
   */
  readonly pageOrigin: Readonly<Ref<string | null>>;
  /** Whether the panel is currently shown on the host page. */
  readonly open: Readonly<Ref<boolean>>;
}

export function useWidget(input: WidgetInput): WidgetView {
  const { publicKey, requestedLocale, pageOrigin, open } = input;

  const failure = ref<'unavailable' | 'offline' | null>(null);
  const appearance = shallowRef<WidgetAppearance | null>(null);
  const prechatFields = shallowRef<readonly WidgetPrechatField[]>([]);
  const conversation = shallowRef<WidgetConversation | null>(null);
  const conversationKnown = ref(false);
  const prechatDone = ref(false);
  const tokenReady = ref(false);

  /**
   * SHALLOW, like every collection below. Each of these is REPLACED by the pure
   * functions in `model/` and never mutated in place, so a deep proxy would buy
   * nothing — and it would hand those functions proxies where they expect the
   * plain arrays their tests exercise.
   */
  const messages = shallowRef<readonly WidgetMessage[]>([]);

  /**
   * THE CHIME, AND THE MARK THAT STOPS IT REPLAYING HISTORY (P2-51).
   *
   * `chimedThrough` is the highest OUTBOUND `seq` this frame has already made a
   * noise for. It starts at null and is seeded on the first thread read rather
   * than chiming for it — a visitor who reloads the page mid-conversation would
   * otherwise hear one chime per reply the agent sent before the reload, all at
   * once.
   *
   * A PLAIN VARIABLE, not a ref: it must not re-render, and it is read and
   * written in the same callback. It lives for the life of this frame — a reload
   * seeds it again, which is what makes the history silent.
   */
  const chime = createChime();
  let chimedThrough: SeqString | null = null;
  const pending = shallowRef<readonly PendingMessage[]>([]);
  const submitting = ref(false);
  const seenSeq = ref<SeqString>(ZERO_SEQ);
  const attempt = ref(0);

  // A visitor should not need a separate "enable sound" control. Their first
  // ordinary interaction with the widget primes its reply chime silently, so a
  // later agent reply can play by default. Capture phase keeps the gesture even
  // when its target immediately changes screen or submits the composer.
  const prime = (): void => {
    document.removeEventListener('pointerdown', prime, true);
    document.removeEventListener('keydown', prime, true);
    chime.prime();
  };
  document.addEventListener('pointerdown', prime, true);
  document.addEventListener('keydown', prime, true);
  onScopeDispose(() => {
    document.removeEventListener('pointerdown', prime, true);
    document.removeEventListener('keydown', prime, true);
  });

  /* ---------------------------------------------------------------------- */
  /* History, the past screen, and the two states a close produces (P2-52)   */
  /* ---------------------------------------------------------------------- */

  const screen = shallowRef<WidgetScreen>({ kind: 'chat' });
  const fresh = ref(false);
  const closing = ref(false);

  const historyItems = shallowRef<readonly WidgetConversationSummary[]>([]);
  const historyLoading = ref(false);
  const historyFailed = ref(false);
  const historyBefore = ref<string | null>(null);

  const pastConversation = shallowRef<WidgetConversation | null>(null);
  const pastMessages = shallowRef<readonly WidgetMessage[]>([]);
  const pastLoading = ref(false);
  const pastFailed = ref(false);

  /**
   * Which request owns each of the two screens.
   *
   * A visitor on a slow connection opens the history, taps a row before it
   * settles, comes back and taps another. Without a ticket the responses land in
   * whatever order the network chose and the LAST one to arrive wins, which is
   * not the one they asked for last. Plain counters rather than refs: the value
   * is read and written inside the same callback and must never cause a render.
   */
  let historyTicket = 0;
  let pastTicket = 0;

  /**
   * THE CONVERSATION THE TRANSCRIPT ON SCREEN BELONGS TO.
   *
   * The one fact that makes a close survivable. `seq` is a gapless
   * PER-CONVERSATION ordinal, so it restarts at 1 in the conversation the next
   * message opens — and `mergeMessages` keys on `id` and orders by `seq`, so
   * merging two conversations interleaves them into one scrambled thread. This
   * is what `reconcileThread` is told about: same id, merge; different id (or
   * none tracked yet), replace.
   *
   * Kept apart from `conversation` on purpose: `conversation` is also written by
   * the bootstrap and by a close, neither of which carries a transcript, and the
   * question being asked here is whether the TRANSCRIPT changed owner.
   */
  let trackedConversationId: string | null = null;

  /**
   * What the visitor clicked in the switcher, or null while they have not. It is
   * SEPARATE from `locale` because `locale` is an answer and this is an input:
   * the reconciliation watcher below recomputes the answer whenever the brand's
   * staffed set arrives, and without a record of the choice it recomputed over
   * the top of it. See `resolveWidgetLocale`'s header for what that looked like
   * on the stand.
   */
  const chosenLocale = ref<WidgetLocale | null>(null);

  const locale = ref<WidgetLocale>(
    resolveWidgetLocale({
      requested: requestedLocale,
      browser: navigator.languages,
      supported: [],
    }),
  );

  /**
   * Answers the visitor already submitted, kept so a locale change can re-open
   * the session without asking for them again. It never leaves this frame's
   * memory and is deliberately not cached: a name and an email address in web
   * storage outlive the conversation they were collected for.
   */
  let submitted: Readonly<Record<string, string | boolean>> | null = null;

  /**
   * How many times a 401 has already sent us back to the bootstrap. Capped,
   * because "token rejected → get a new token → token rejected" is a loop that
   * hammers a T1 route from every visitor's browser at once.
   */
  let reauthorisations = 0;

  /**
   * What this visitor has already decided, read once per widget key — at setup,
   * BEFORE the first render and before any watcher runs.
   *
   * The ordering matters: a consent state read one tick late is a tick in which
   * `isGranted` answers false for a purpose the visitor granted — and the
   * watcher that sends the context is keyed on this value, so a late read would
   * fire one PUT with the two fields absent and then a second with them present.
   * Two requests, and the first one clears a column the visitor had agreed to
   * fill. The read itself absorbs every way storage can refuse and degrades to
   * `noConsent()`, which is the safe direction.
   */
  const consent = shallowRef<ConsentState>(readConsent(publicKey));

  /**
   * The read pointer is PER CONVERSATION (P2-52), so this re-reads whenever the
   * visitor's current conversation changes — a close followed by a new message
   * is a new seq space, and the pointer the old one left behind would mark the
   * new conversation's first replies as already read.
   */
  const conversationId = computed(() => conversation.value?.id ?? null);
  watch(
    conversationId,
    (id) => {
      seenSeq.value = readSeenSeq(publicKey, id);
    },
    { immediate: true },
  );

  /**
   * What the browser will say about itself, read ONCE per frame — recomputed
   * only when `pageOrigin` changes, which it does exactly once, at the handshake.
   *
   * None of these values can change between two renders of a chat panel. A
   * browser is not updated mid-conversation, and re-reading
   * `navigator.userAgentData` on every keystroke in the composer would re-run
   * the whole collection for a value that cannot have moved.
   *
   * THE VIEWPORT IS THE ONE THAT GENUINELY CAN CHANGE — a visitor rotates a
   * phone — and it is read once anyway, because the only thing derived from it
   * here is `deviceClass`, and a `deviceClass` that flipped mid-conversation
   * would rewrite a stored fact about a person every time somebody turned their
   * wrist. The value on the card is a snapshot with an "as of" beside it, which
   * is what makes reading it once honest rather than lossy.
   *
   * `pageOrigin` is passed and the other two host signals are not, because the
   * other two have no transport: `HostMessage` carries no page fields. It is
   * NOT sent to the server — `visitorContextBody` drops it, since
   * `widget_session.origin` already holds a browser-vouched copy — and it is
   * passed in anyway so that the day a path arrives, every rule in
   * `visitor-context.ts` is already applied to it at this call site rather than
   * at a new one.
   */
  const signals = computed(() =>
    readVisitorSignals(pageOrigin.value === null ? {} : { pageOrigin: pageOrigin.value }),
  );

  const consentPurposes = computed(() => availablePurposes(signals.value));

  /**
   * One decision, recorded and mirrored.
   *
   * `grant` and `revoke` from `consent.ts` do the deciding — this only supplies
   * the clock and the storage, which is exactly the split that module asks for.
   * `Date` is constructed here rather than inside the module because a consent
   * record whose timestamp came from inside the function is a record nobody can
   * test and nobody can reproduce in a support conversation.
   */
  const decide = (purpose: ConsentPurpose, granted: boolean): void => {
    const next = granted
      ? grant(consent.value, purpose, new Date())
      : revoke(consent.value, purpose, new Date());
    writeConsent(publicKey, next);
    consent.value = next;
  };

  const grantConsent = (purpose: ConsentPurpose): void => {
    decide(purpose, true);
  };

  /**
   * A refusal AND a withdrawal, deliberately one function.
   *
   * `revoke` is the same call for both — `consent.ts` argues that a flag "has no
   * revocation, only a second write that looks exactly like the first", and the
   * fix there was to record `granted: false` as a real answer rather than to
   * have two verbs. The visitor-facing difference is which control they clicked;
   * the recorded fact is identical, and so is the consequence: the next
   * `visitorContextBody` omits the field and the next PUT clears the column.
   */
  const refuseConsent = (purpose: ConsentPurpose): void => {
    decide(purpose, false);
  };

  /* ---------------------------------------------------------------------- */
  /* Identity — the site's signed-in player (ADR-0249 §4, P2-127)            */
  /* ---------------------------------------------------------------------- */

  /**
   * The JWT the site last handed us for its signed-in player, or null.
   *
   * MEMORY ONLY, never `sessionStorage`. It is a credential the site minted for
   * a short life, and the site re-sends it on every page view anyway (its
   * `identify` on mount, or `init.jwt`). Kept here, rather than spent once, so
   * that a session that expires mid-visit re-bootstraps as the same player while
   * the token's own `exp` still allows it; once it does not, the server answers
   * `identity_invalid` and the widget carries on anonymously.
   */
  let userJwt: string | null = null;
  /**
   * The `sub` the session this tab holds was minted for, or null when it is an
   * anonymous visitor's. Unverified on this side — see `jwtSubject` — and used
   * only to recognise "the same player again".
   */
  let currentSubject: string | null = null;
  /** `identify` calls waiting on the next bootstrap that carries `userJwt`. */
  let identityWaiters: ((outcome: IdentityOutcome) => void)[] = [];
  /**
   * The edge refused the token a sign-in tried to replace (it had already
   * expired), so the retry goes without it. Nothing is lost: a dead session has
   * nothing to revoke.
   */
  let replaceWithoutBearer = false;

  const settleIdentity = (outcome: IdentityOutcome): void => {
    const waiting = identityWaiters;
    identityWaiters = [];
    for (const resolve of waiting) {
      resolve(outcome);
    }
  };

  /**
   * Everything on screen belonged to the person this tab WAS. A sign-in or a
   * sign-out changes who that is, and the previous person's transcript, history
   * and pre-chat answers must not stay in front of the next one — on a shared
   * screen that is the whole point of signing out.
   */
  const forgetPerson = (): void => {
    messages.value = [];
    pending.value = [];
    conversation.value = null;
    conversationKnown.value = false;
    trackedConversationId = null;
    chimedThrough = null;
    fresh.value = false;
    screen.value = { kind: 'chat' };
    historyTicket += 1;
    pastTicket += 1;
    historyItems.value = [];
    historyBefore.value = null;
    pastConversation.value = null;
    pastMessages.value = [];
    submitted = null;
    prechatDone.value = false;
  };

  /* ---------------------------------------------------------------------- */
  /* Session                                                                 */
  /* ---------------------------------------------------------------------- */

  watch(
    [pageOrigin, locale, attempt],
    ([origin, currentLocale], _previous, onCleanup) => {
      // Nothing is requested before the handshake completes.
      //
      // `pageOrigin` is the value `allowed_origins` is checked against, and this
      // gate is why it can be trusted as far as it is.
      //
      // It is NOT read from `location` or from a config the page supplies. It
      // comes from `event.origin` of the loader's postMessage handshake, which
      // the browser stamps on the message and the sender cannot set — so within
      // a browser it genuinely names the embedding page. The `Origin` header
      // cannot do this job: the request is made by THIS iframe, so the header
      // says `widget.<domain>` on every request from every customer's site.
      //
      // Until the handshake lands there is nothing honest to send, so nothing is
      // requested. A widget that guessed its host origin here would be handing
      // the server a value it made up, which is the one thing that would make the
      // check meaningless rather than merely bounded.
      if (origin === null) {
        return;
      }

      const controller = new AbortController();
      let live = true;
      onCleanup(() => {
        live = false;
        controller.abort();
      });

      const jwt = userJwt;
      const subject = jwt === null ? null : jwtSubject(jwt);
      const cached = readCachedSession(publicKey, Date.now());
      if (
        cached !== null &&
        cached.locale === currentLocale &&
        submitted === null &&
        // A signed-in player reuses a session only if it was minted for THEM.
        // With no JWT, whatever this tab holds stands — a player's session
        // included — until the site calls `logout()`, which is Intercom's
        // contract too: `shutdown` is what ends a signed-in session.
        (jwt === null || (subject !== null && cached.subject === subject))
      ) {
        // The whole reason the cache exists: `POST /session` is rate tier T1 and
        // costs a person row, and the iframe is rebuilt on every navigation of
        // the customer's site. Reusing an unexpired token turns four page views
        // into one session instead of four.
        setVisitorToken(cached.token);
        currentSubject = cached.subject;
        appearance.value = cached.appearance;
        prechatFields.value = cached.prechatFields;
        tokenReady.value = true;
        if (jwt !== null) {
          settleIdentity(IDENTITY_OK);
        }
        return;
      }

      const answers = submitted;
      // A sign-in replaces whatever session this tab holds, and says which, so
      // the server can end it (ADR-0249 §4). A fresh frame has the token only in
      // the cache until now.
      if (jwt !== null && cached !== null && !hasVisitorToken()) {
        setVisitorToken(cached.token);
      }
      const replacing = jwt !== null && !replaceWithoutBearer && hasVisitorToken();
      const conversationWasPolled = tokenReady.value;

      void openSession(
        {
          publicKey,
          pageOrigin: origin,
          locale: currentLocale,
          ...(answers === null ? {} : { prechat: answers }),
          ...(jwt === null ? {} : { userJwt: jwt }),
        },
        { signal: controller.signal, replacing },
      )
        .then((session) => {
          if (!live) {
            return;
          }
          writeCachedSession(publicKey, {
            token: session.visitorToken,
            expiresAt: session.expiresAt,
            locale: currentLocale,
            appearance: session.appearance,
            prechatFields: session.prechatFields,
            subject,
          });
          const changedPerson = subject !== currentSubject;
          if (changedPerson) {
            forgetPerson();
          }
          currentSubject = subject;
          replaceWithoutBearer = false;
          appearance.value = session.appearance;
          prechatFields.value = session.prechatFields;
          conversation.value = session.conversation;
          conversationKnown.value = true;
          tokenReady.value = true;
          failure.value = null;
          reauthorisations = 0;
          if (jwt !== null) {
            settleIdentity(IDENTITY_OK);
          }
          if (changedPerson && conversationWasPolled) {
            // A poll started under the previous person's token may still be in
            // flight; restarting the poll retires it, so its answer cannot
            // repaint the transcript this tab just forgot. The re-run of this
            // watcher finds the session just cached and spends nothing.
            attempt.value += 1;
          }
        })
        .catch((cause: unknown) => {
          if (!live || controller.signal.aborted) {
            return;
          }
          if (jwt !== null) {
            if (replacing && isStaleBearer(cause)) {
              // The token this tab held was already dead, and the edge refused
              // it before the JWT was read. Once more without it.
              replaceWithoutBearer = true;
              clearCachedSession(publicKey);
              setVisitorToken(null);
              tokenReady.value = false;
              attempt.value += 1;
              return;
            }
            // The sign-in failed. The visitor stays exactly who they were: the
            // server revokes the old session only after a JWT verifies, so a
            // session this tab already holds still works. Without one — a
            // first boot from `init.jwt` — the widget comes up anonymous, so
            // that `ready` still reaches the loader and its queue moves on.
            userJwt = null;
            settleIdentity({ ok: false, reason: identityFailure(cause) });
            if (!tokenReady.value) {
              attempt.value += 1;
            }
            return;
          }
          // A PROBLEM DOCUMENT means the API understood us and said no. The
          // session route answers 404 for an unknown key, a revoked key and an
          // origin outside the allowlist ALIKE — deliberately, so that a key
          // cannot be used to enumerate which sites may embed it — so there is
          // nothing to retry and nothing more specific to say. A transport
          // failure is the opposite: nothing was decided, and trying again is
          // exactly right.
          failure.value = cause instanceof ApiProblemError ? 'unavailable' : 'offline';
        });
    },
    { immediate: true },
  );

  /**
   * Once the brand's staffed locales are known the language may need to change,
   * and if it does the watcher above runs again — because tenant copy is
   * resolved by the SERVER against the locale the bootstrap sent, and the first
   * call asked with a provisional guess. One extra request, only when the guess
   * was wrong. A ref assigned its current value does not trigger, so a guess
   * that was right costs nothing.
   */
  watch(
    [appearance, chosenLocale],
    ([currentAppearance, chosen]) => {
      if (currentAppearance === null) {
        return;
      }
      locale.value = resolveWidgetLocale({
        chosen,
        requested: requestedLocale,
        browser: navigator.languages,
        supported: currentAppearance.locales,
      });
    },
    { immediate: true },
  );

  /* ---------------------------------------------------------------------- */
  /* Transcript                                                              */
  /* ---------------------------------------------------------------------- */

  const reauthorise = (): void => {
    // The cached credential is gone as far as the server is concerned; keeping
    // it would mean every request in this tab 401s until it expires.
    clearCachedSession(publicKey);
    setVisitorToken(null);
    tokenReady.value = false;

    if (reauthorisations >= 1) {
      failure.value = 'unavailable';
      return;
    }
    reauthorisations += 1;
    attempt.value += 1;
  };

  /**
   * Take a conversation and a page of its messages from wherever they arrived —
   * the poll, or the response to a send — and decide whether that is still the
   * same conversation (P2-52, ADR-0142).
   *
   * THE THREE THINGS A CHANGE COSTS, and each of them is a bug if it is
   * forgotten:
   *
   *   THE TRANSCRIPT is replaced rather than merged. `reconcileThread`'s header
   *   has the arithmetic: two conversations' seqs both start at 1, so a merge
   *   interleaves them and the visitor reads last week's answer under today's
   *   question.
   *
   *   THE CHIME'S WATERMARK goes back to null, which makes the first read of the
   *   new conversation seed it silently. Leaving it would carry the old
   *   conversation's highest outbound seq — say 7 — into a space that starts at
   *   1, and every reply up to the seventh would arrive without a sound.
   *
   *   `fresh` CLEARS, because the blank page it was standing in for is now a
   *   real conversation. It clears HERE and nowhere else: a new id is the only
   *   evidence that the send actually created one.
   *
   * It does NOT touch `pending`. The two callers retire a pending message
   * differently — the poll by body, one for one; the send by its idempotency
   * key, exactly — and doing both to one response clears two entries for one
   * confirmation.
   */
  const adoptThread = (
    incoming: WidgetConversation | null,
    incomingMessages: readonly WidgetMessage[],
  ): void => {
    const incomingId = incoming?.id ?? null;
    const sameConversation = incomingId !== null && incomingId === trackedConversationId;

    if (!sameConversation) {
      trackedConversationId = incomingId;
      chimedThrough = null;
      fresh.value = false;
    }

    conversation.value = incoming;
    conversationKnown.value = true;
    messages.value = reconcileThread(messages.value, incomingMessages, { sameConversation });
  };

  watch(
    [tokenReady, open, attempt],
    ([ready, isOpen], _previous, onCleanup) => {
      if (!ready) {
        return;
      }

      let live = true;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let consecutiveFailures = 0;
      onCleanup(() => {
        live = false;
        if (timer !== undefined) {
          clearTimeout(timer);
        }
      });

      const poll = async (): Promise<void> => {
        try {
          const thread = await fetchThread({ limit: THREAD_PAGE_SIZE });
          if (!live) {
            return;
          }
          consecutiveFailures = 0;
          // `adoptThread` and not three assignments: it is the one place that
          // knows a new conversation id means REPLACE the transcript rather than
          // merge into it, and that a `fresh` blank page ends when that id
          // arrives. A poll that is still reading the same conversation the
          // visitor closed leaves `fresh` alone, which is what keeps the closed
          // transcript from reappearing under a composer they were about to type
          // in.
          adoptThread(thread.conversation, thread.messages);
          pending.value = clearConfirmed(pending.value, thread.messages);

          /*
           * A REPLY ARRIVED. The sound is unconditional on panel state, which
           * is the owner’s call: a visitor who collapsed the panel is exactly
           * the one who needs telling, and one who is watching it has already
           * been told by the message appearing — harmlessly, in a chat they
           * chose to open.
           *
           * SEEDED ON THE FIRST READ, never chimed for: `chimedThrough` is null
           * on the first read of each conversation, and that read is the
           * history. `adoptThread` above is what puts it back to null when the
           * conversation changes, so the seq space the watermark lives in is
           * always the one being compared against.
           */
          const outbound = latestOutboundSeq(thread.messages);
          const previous = chimedThrough;
          chimedThrough = outbound;
          if (previous !== null && compareSeq(outbound, previous) > 0) {
            chime.play();
          }
          if (failure.value === 'offline') {
            failure.value = null;
          }
        } catch (cause) {
          if (!live) {
            return;
          }
          if (cause instanceof ApiProblemError && cause.status === 401) {
            // The token expired mid-session, or the key was rotated past its
            // `retiring_until`. Both are recoverable exactly once.
            reauthorise();
            return;
          }
          consecutiveFailures += 1;
          // Deliberately not surfaced on the first failure: a widget that
          // flashes "we lost you" every time a phone changes cell tower is a
          // widget people learn to distrust. It is still recorded, so the
          // console of a visitor who reports a problem says what happened.
          console.debug('[omni-widget] thread poll failed', cause);
          if (consecutiveFailures >= POLL_FAILURES_BEFORE_OFFLINE) {
            failure.value = 'offline';
          }
        } finally {
          if (live) {
            timer = setTimeout(
              () => {
                void poll();
              },
              isOpen ? POLL_INTERVAL_OPEN_MS : POLL_INTERVAL_CLOSED_MS,
            );
          }
        }
      };

      void poll();
    },
    { immediate: true },
  );

  /**
   * Opening the panel is what marks replies as read — not receiving them. The
   * pointer only ever moves forward, which is enforced in `writeSeenSeq`, so a
   * page of scrollback cannot un-read something.
   */
  watchEffect(() => {
    if (!open.value) {
      return;
    }
    const top = latestSeq(messages.value);
    if (compareSeq(top, seenSeq.value) <= 0) {
      return;
    }
    writeSeenSeq(publicKey, conversationId.value, top);
    seenSeq.value = top;
  });

  /* ---------------------------------------------------------------------- */
  /* Visitor context                                                         */
  /* ---------------------------------------------------------------------- */

  /**
   * Tell the API what this browser reported — once there is a question to help
   * with, and again whenever the visitor changes their mind.
   *
   * ── WHY THIS IS NOT ON THE BOOTSTRAP ────────────────────────────────────
   *
   * The obvious place is the `openSession` body: the person exists there, the
   * request is already going out, and it would cost no second round trip. It is
   * the wrong place, and the reason is the lawful basis rather than the wiring.
   *
   * The necessary set is collected without consent because of Art. 6(1)(b) —
   * processing necessary to provide a service THE DATA SUBJECT ASKED FOR — and
   * `visitor-context.ts` states what the asking is: "the visitor opened a chat
   * panel and typed a question". The bootstrap happens on the postMessage
   * handshake, which happens on PAGE LOAD, for every visitor of the customer's
   * site whether or not they ever click the launcher. Attaching device facts
   * there would collect them from people who asked for nothing — which is the
   * passive measurement this whole module refuses, arrived at by putting a field
   * in a convenient body.
   *
   * So the gate is `conversation !== null`: there is a thread, so there is a
   * question, so there is something to help with. A visitor who never opens the
   * panel is never measured, and nothing needs to remember that.
   *
   * ── WHY IT RE-RUNS ON `consent` ─────────────────────────────────────────
   *
   * Because a withdrawal has to reach the database. `revoke` takes effect
   * immediately in this frame, and `consent.ts` notes that "a caller that has
   * cached a built context must rebuild it"; the watch source IS the rebuild.
   * The PUT replaces the whole snapshot, so a body whose two optional keys have
   * gone absent clears both columns in one statement — the same request shape as
   * a grant, with two fewer keys, which is Art. 7(3) expressed as wiring.
   *
   * ── ONE-WAY, AND FAILURES ARE SILENT ────────────────────────────────────
   *
   * Nothing in the panel depends on the response. `recordedAt` is for the
   * agent's card, not for the visitor, so a failed PUT must not produce an
   * error state in a chat window — a visitor told "something went wrong" about a
   * request they did not make and cannot retry learns only that this widget is
   * unreliable. It is logged at debug, exactly as a failed thread poll is, so
   * the console of a visitor who reports a problem says what happened.
   *
   * A 401 is NOT routed into `reauthorise` here, and that is deliberate: the
   * poll owns that recovery, runs on an interval, and will reach the same
   * conclusion within eight seconds. Two callers racing to re-bootstrap on one
   * expired token is how a capped retry loop gets uncapped.
   */
  const hasConversation = computed(() => conversation.value !== null);
  watch(
    [tokenReady, hasConversation, signals, consent],
    ([ready, has, currentSignals, currentConsent], _previous, onCleanup) => {
      if (!ready || !has) {
        return;
      }
      const controller = new AbortController();
      onCleanup(() => {
        controller.abort();
      });
      void putVisitorContext(visitorContextBody(currentSignals, currentConsent), {
        signal: controller.signal,
      }).catch((cause: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        console.debug('[omni-widget] visitor context not recorded', cause);
      });
    },
    { immediate: true },
  );

  /* ---------------------------------------------------------------------- */
  /* Actions                                                                 */
  /* ---------------------------------------------------------------------- */

  const submitPrechat = (values: PrechatValues): void => {
    // Unreachable in practice — the form only renders after a bootstrap, which
    // this same value gates. Checked anyway rather than asserted away: the
    // alternative is a non-null assertion on the field the server's origin
    // check reads, and that is the last value in this app to paper over.
    const origin = pageOrigin.value;
    if (origin === null) {
      return;
    }

    const payload = prechatPayload(prechatFields.value, values);
    submitted = payload;
    submitting.value = true;
    const currentLocale = locale.value;

    // A signed-in player's form answers must not sign them out: the same JWT
    // goes along, and the session it replaces is named (ADR-0249 §4).
    const jwt = userJwt;
    const subject = jwt === null ? null : jwtSubject(jwt);
    void openSession(
      {
        publicKey,
        pageOrigin: origin,
        locale: currentLocale,
        prechat: payload,
        ...(jwt === null ? {} : { userJwt: jwt }),
      },
      { replacing: jwt !== null && hasVisitorToken() },
    )
      .then((session) => {
        writeCachedSession(publicKey, {
          token: session.visitorToken,
          expiresAt: session.expiresAt,
          locale: currentLocale,
          appearance: session.appearance,
          prechatFields: session.prechatFields,
          subject,
        });
        currentSubject = subject;
        appearance.value = session.appearance;
        prechatFields.value = session.prechatFields;
        conversation.value = session.conversation;
        conversationKnown.value = true;
        prechatDone.value = true;
        tokenReady.value = true;
        failure.value = null;
      })
      .catch((cause: unknown) => {
        failure.value = cause instanceof ApiProblemError ? 'unavailable' : 'offline';
      })
      .finally(() => {
        submitting.value = false;
      });
  };

  const deliver = (clientMessageId: string, body: string): void => {
    void sendMessage({ clientMessageId, body })
      .then((result) => {
        // THE SEND IS WHERE A NEW CONVERSATION IS BORN (ADR-0142): after a
        // close, this response is the first thing that names it. Routing it
        // through `adoptThread` is what turns the visitor's blank `fresh` page
        // into a real one-message transcript instead of merging that message
        // into the closed conversation still held underneath.
        adoptThread(result.conversation, [result.message]);
        /*
         * AND `fresh` ENDS HERE TOO, whether or not the id changed.
         *
         * `adoptThread` ends it on a new conversation, which is the normal
         * path. The case it cannot see is an AGENT REOPENING the conversation
         * the visitor had closed: the status goes back to `open` under the same
         * id, so the server appends to it rather than creating a new one, and
         * the send that the blank page existed for lands in the conversation
         * already on screen. Left `fresh`, the visitor would watch their
         * message leave the composer and appear nowhere.
         */
        fresh.value = false;
        // By key, not by body: this is the exact path, and it is the one that
        // makes a retry of a message that DID arrive resolve cleanly — the
        // server recognises the idempotency key and returns the message it
        // already stored, `deduplicated: true`, with the same seq.
        pending.value = clearByClientMessageId(pending.value, clientMessageId);
      })
      .catch((cause: unknown) => {
        console.debug('[omni-widget] send failed', cause);
        // The pending entry STAYS, holding the text the visitor wrote. Dropping
        // it and showing a toast loses their message, which is the one thing a
        // chat widget must never do.
        pending.value = pending.value.map((message) =>
          message.clientMessageId === clientMessageId
            ? { ...message, state: 'failed' as const }
            : message,
        );
      });
  };

  const send = (body: string): void => {
    const text = body.trim();
    if (text === '') {
      return;
    }
    const clientMessageId = newClientMessageId();
    pending.value = [
      ...pending.value,
      { clientMessageId, body: text, state: 'sending', startedAt: Date.now() },
    ];
    deliver(clientMessageId, text);
  };

  const retry = (clientMessageId: string): void => {
    const failed = pending.value.find((message) => message.clientMessageId === clientMessageId);
    if (failed === undefined) {
      return;
    }
    pending.value = pending.value.map((message) =>
      message.clientMessageId === clientMessageId
        ? { ...message, state: 'sending' as const }
        : message,
    );
    // THE SAME clientMessageId. That is what makes this a retry rather than a
    // second message: if the first attempt did reach the server before the
    // connection dropped, the unique index recognises the key and the server
    // returns the message it already stored instead of writing another one.
    deliver(clientMessageId, failed.body);
  };

  const setLocale = (next: WidgetLocale): void => {
    // BOTH, and the first one is the fix. Setting only `locale` left the choice
    // invisible to the reconciliation watcher above, which re-derives the locale
    // from the browser and the staffed set every time `appearance` changes —
    // and changing `locale` re-runs the bootstrap, which sets `appearance`. So
    // the switcher moved the locale and its own consequence moved it back, with
    // nothing logged.
    chosenLocale.value = next;
    locale.value = next;
  };

  const reconnect = (): void => {
    failure.value = null;
    attempt.value += 1;
  };

  /* ---------------------------------------------------------------------- */
  /* History, past, and ending a chat (P2-52)                                */
  /* ---------------------------------------------------------------------- */

  /**
   * One page of history. `before === null` means the FIRST page, and the first
   * page REPLACES rather than merges: opening the screen is a refresh, and a
   * refresh that merged would keep showing a conversation the visitor deleted
   * their way out of, or leave `nextBefore` describing a page that no longer
   * starts where it did.
   *
   * A failure sets a flag and logs at debug, exactly as a failed poll does. This
   * is a screen the visitor navigated to deliberately, so unlike the poll it
   * says so on the first failure — there is nothing else on the screen to read.
   */
  const loadHistoryPage = (before: string | null): void => {
    historyTicket += 1;
    const ticket = historyTicket;
    historyLoading.value = true;
    historyFailed.value = false;

    void fetchHistory({ limit: HISTORY_PAGE_SIZE, ...(before === null ? {} : { before }) })
      .then((page) => {
        // A response from a request the visitor has already navigated past must
        // not paint over the one they are waiting for.
        if (historyTicket !== ticket) {
          return;
        }
        historyItems.value =
          before === null
            ? page.conversations
            : mergeHistory(historyItems.value, page.conversations);
        historyBefore.value = page.nextBefore;
      })
      .catch((cause: unknown) => {
        if (historyTicket !== ticket) {
          return;
        }
        console.debug('[omni-widget] history read failed', cause);
        historyFailed.value = true;
      })
      .finally(() => {
        if (historyTicket === ticket) {
          historyLoading.value = false;
        }
      });
  };

  const openHistory = (): void => {
    screen.value = { kind: 'history' };
    // Loaded on open and RELOADED on every open, rather than cached for the life
    // of the frame: the list's whole content is "what happened recently", and an
    // hour-old copy of that is the one thing it must not show.
    loadHistoryPage(null);
  };

  /**
   * The next page — and, when the first one failed, the retry for it.
   *
   * One function for both because `historyBefore` already distinguishes them: it
   * is null until a page has succeeded, so a retry asks for the first page again
   * and a scroll to the bottom asks for the next one. The guard is what stops an
   * IntersectionObserver that fires twice from asking for the same page twice.
   */
  const loadMoreHistory = (): void => {
    if (historyLoading.value) {
      return;
    }
    loadHistoryPage(historyBefore.value);
  };

  const openPast = (id: string): void => {
    pastTicket += 1;
    const ticket = pastTicket;

    screen.value = { kind: 'past', conversationId: id };
    // Cleared rather than left in place: the previous conversation's messages
    // under a spinner read as this conversation's, and a visitor who taps two
    // rows in a row would be shown the wrong transcript in between.
    pastConversation.value = null;
    pastMessages.value = [];
    pastFailed.value = false;
    pastLoading.value = true;

    void fetchPastThread(id, { limit: THREAD_PAGE_SIZE })
      .then((thread) => {
        if (pastTicket !== ticket) {
          return;
        }
        pastConversation.value = thread.conversation;
        pastMessages.value = thread.messages;
      })
      .catch((cause: unknown) => {
        if (pastTicket !== ticket) {
          return;
        }
        // A 404 here is the RESTRICTIVE policy answering "not yours", and it is
        // deliberately indistinguishable from "does not exist". Either way there
        // is one thing to say and one thing to offer: it could not be read, and
        // the way back to the list.
        console.debug('[omni-widget] past conversation read failed', cause);
        pastFailed.value = true;
      })
      .finally(() => {
        if (pastTicket === ticket) {
          pastLoading.value = false;
        }
      });
  };

  const backToChat = (): void => {
    screen.value = { kind: 'chat' };
  };

  /**
   * End the current chat.
   *
   * NOT routed through `adoptThread`: the route returns the same conversation
   * with a new status and NO messages, so adopting it would be adopting an empty
   * page of a conversation that has not changed — and while the id is the same
   * that merges nothing, the day somebody "simplifies" it into the adopt path is
   * the day it starts wiping the transcript the visitor is still reading.
   *
   * A FAILURE SAYS NOTHING. The visitor asked to leave; telling them it did not
   * work leaves them in a chat they have mentally left, and the operation is
   * idempotent, so tapping again is a complete recovery. It is logged at debug
   * like every other absorbed failure in this composable.
   */
  const endChat = (): void => {
    closing.value = true;
    void closeConversation()
      .then((closed) => {
        conversation.value = closed;
        conversationKnown.value = true;
      })
      .catch((cause: unknown) => {
        console.debug('[omni-widget] close failed', cause);
      })
      .finally(() => {
        closing.value = false;
      });
  };

  /**
   * "Start a new conversation" — which the server is told nothing about.
   *
   * There is no route to call. `widget.messages.send` creates the conversation
   * itself when the current one is closed, so a "create conversation" request
   * would either be a second way to do the same thing or a row created for a
   * visitor who changed their mind before typing. What this flips is a local
   * blank page; the send is what makes it real, and the new id coming back is
   * what clears the flag (see `adoptThread`).
   */
  const startNewConversation = (): void => {
    fresh.value = true;
  };

  /**
   * The site's player signed in (ADR-0249 §4) — from `init.jwt` or an
   * `identify` message, the same path either way.
   *
   * Already this player's session → nothing to do, and the bootstrap is not
   * spent. Otherwise the JWT is held and the session watcher is nudged: it
   * aborts whatever bootstrap is in flight, mints the player's session, and
   * settles this promise from the outcome.
   */
  const identify = (jwt: string): Promise<IdentityOutcome> => {
    userJwt = jwt;
    const subject = jwtSubject(jwt);
    if (tokenReady.value && subject !== null && subject === currentSubject) {
      return Promise.resolve(IDENTITY_OK);
    }
    return new Promise<IdentityOutcome>((resolve) => {
      identityWaiters.push(resolve);
      attempt.value += 1;
    });
  };

  /**
   * The site's player signed out (ADR-0249 §4): end the session on the server,
   * forget the device, and come back as a new anonymous visitor.
   *
   * THE LOCAL HALF HAPPENS WHATEVER THE SERVER SAID. A sign-out that kept the
   * token because a request failed would leave the player signed in on a shared
   * screen; the outcome still reports the failure, so the site knows the server
   * side may not have happened.
   *
   * A session that had already expired cannot end itself (the route needs a
   * live token), but the device may still hold the continuity cookie. One
   * anonymous bootstrap resumes it — which is what the cookie is for — and that
   * session then ends it.
   */
  const logout = async (): Promise<IdentityOutcome> => {
    userJwt = null;
    let outcome: IdentityOutcome = IDENTITY_OK;
    const cached = readCachedSession(publicKey, Date.now());
    if (!hasVisitorToken() && cached !== null) {
      setVisitorToken(cached.token);
    }
    if (hasVisitorToken()) {
      try {
        await endSession();
      } catch (cause) {
        if (isStaleBearer(cause) && pageOrigin.value !== null) {
          try {
            setVisitorToken(null);
            await openSession({ publicKey, pageOrigin: pageOrigin.value, locale: locale.value });
            await endSession();
          } catch (retryCause) {
            console.debug('[omni-widget] sign-out could not reach the server', retryCause);
            outcome = { ok: false, reason: 'unavailable' };
          }
        } else {
          console.debug('[omni-widget] sign-out could not reach the server', cause);
          outcome = { ok: false, reason: identityFailure(cause) };
        }
      }
    }

    clearCachedSession(publicKey);
    setVisitorToken(null);
    currentSubject = null;
    tokenReady.value = false;
    forgetPerson();
    // A new anonymous visitor. The server cleared the continuity cookie, so
    // this bootstrap resumes nobody.
    attempt.value += 1;
    return outcome;
  };

  const history = computed<WidgetHistoryView>(() => ({
    items: historyItems.value,
    loading: historyLoading.value,
    failed: historyFailed.value,
    hasMore: historyBefore.value !== null,
    loadMore: loadMoreHistory,
  }));

  const past = computed<WidgetPastView>(() => ({
    conversation: pastConversation.value,
    messages: pastMessages.value,
    loading: pastLoading.value,
    failed: pastFailed.value,
  }));

  /**
   * UNREAD IS COUNTED ON THE REAL TRANSCRIPT, not on the one `fresh` shows.
   *
   * While the visitor is looking at a blank page, the messages underneath are
   * still the closed conversation's — messages they have already read, whose
   * pointer is already past them. Counting the blank list instead would be
   * counting zero, which is the same answer by accident and the wrong answer the
   * moment a reply lands on a conversation somebody reopened.
   */
  const unread = computed(() => unreadCount(messages.value, seenSeq.value));

  const orderedPrechatFields = computed(() => orderedFields(prechatFields.value));

  const phase = computed(() =>
    resolvePhase({
      failure: failure.value,
      ready: tokenReady.value && appearance.value !== null,
      conversationKnown: conversationKnown.value,
      hasConversation: conversation.value !== null,
      hasPrechat: orderedPrechatFields.value.length > 0,
      prechatDone: prechatDone.value,
    }),
  );

  /**
   * ONE REACTIVE OBJECT, read as plain properties.
   *
   * `reactive` unwraps every ref and computed it is given, so a component reads
   * `view.phase` — not `view.phase.value` — and the read is tracked. What it
   * returns for an unwrapped ref is that ref's own value, so the shallow arrays
   * above reach the screens as the same plain arrays `model/` produced, never
   * as proxies.
   */
  return reactive({
    phase,
    appearance,
    prechatFields: orderedPrechatFields,
    conversation,
    locale,
    // THE BLANK PAGE IS HERE AND NOWHERE ELSE. `messages` in this composable
    // stays the closed conversation's transcript while `fresh` — the poll keeps
    // merging into it, because the tracked id has not changed — and what the
    // panel is handed is empty until a new conversation id arrives. One
    // substitution, at the boundary, rather than a `fresh` branch in every
    // consumer.
    messages: computed(() => (fresh.value ? NO_MESSAGES : messages.value)),
    pending,
    unread,
    submitting,
    screen,
    history,
    past,
    fresh,
    closing,
    consent,
    consentPurposes,
    grantConsent,
    refuseConsent,
    setLocale,
    submitPrechat,
    send,
    retry,
    reconnect,
    openHistory,
    openPast,
    backToChat,
    endChat,
    startNewConversation,
    identify,
    logout,
  });
}

/**
 * The phase is DERIVED, not stored.
 *
 * Storing it would mean every one of the places above that learns something
 * also has to remember to re-decide the screen, and the one that forgets leaves
 * the visitor on a spinner that will never resolve. Derived, the screen is a
 * function of what is known, and "what is known" is the only thing anyone has to
 * get right.
 */
function resolvePhase(state: {
  readonly failure: 'unavailable' | 'offline' | null;
  readonly ready: boolean;
  readonly conversationKnown: boolean;
  readonly hasConversation: boolean;
  readonly hasPrechat: boolean;
  readonly prechatDone: boolean;
}): WidgetPhase {
  if (state.failure !== null) {
    return { kind: state.failure };
  }
  if (!state.ready || !state.conversationKnown) {
    return { kind: 'booting' };
  }
  // A visitor who already has a thread is never asked to introduce themselves
  // again — the form's job was to identify them, and the conversation proves it
  // was done.
  if (!state.hasConversation && state.hasPrechat && !state.prechatDone) {
    return { kind: 'prechat' };
  }
  return { kind: 'ready' };
}
